import pick from 'lodash/pick.js';
import uuid from 'uuid/v4.js';

import { handleResponseError } from '@/Helpers.js';
import Model from '@/models/Model.js';
import TermTemplate from '@/models/term-template/TermTemplate.js';
import TermTemplatePeriodCourseTemplate from '@/models/term-template/TermTemplatePeriodCourseTemplate.js';

export default class TermTemplatePeriod extends Model {
    static entity = 'term_template_period';

    static modelName = 'TermTemplatePeriod';

    static primaryKey = 'uuid';

    static fields() {
        return {
            id: this.number(null).nullable(),
            term_schedule_template_id: this.attr(null),
            uuid: this.attr(() => uuid()),
            title: this.string(''),
            weeks: this.number(null).nullable(),
            order: this.number(0),
            period_order: this.number(0).nullable(),
            type: this.attr(null),

            ...super.fields(),

            courses: this.hasMany(TermTemplatePeriodCourseTemplate, 'term_schedule_template_period_id', 'id'),
            parent: this.belongsTo(TermTemplate, 'term_schedule_template_id', 'id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    static resetAll() {
        return this.deleteAll();
    }

    // API - GET
    static async fetch(id, termTemplateId) {
        const endpoint = `/api/term-templates/${termTemplateId}/periods/${id}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // Fetch a Required Term from the Store by Refreshing the API
    static async getPeriodByIdWithCourses(id, termTemplateId) {
        await this.fetch(id, termTemplateId);

        return this.query().with('courses').whereId(id).first();
    }

    // API - PUT
    static async sync(termTemplatePeriod, data) {
        const endpoint = `/api/term-templates/${termTemplatePeriod.term_schedule_template_id}/periods/${termTemplatePeriod.id}`;

        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status === 200) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId: termTemplatePeriod.id,
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - DELETE
    static async remove(termTemplatePeriod) {
        const endpoint = `/api/term-templates/${termTemplatePeriod.term_schedule_template_id}/periods/${termTemplatePeriod.id}`;
        try {
            const { response } = await this.api().delete(endpoint);

            if (response.status === 200) {
                await this.delete(termTemplatePeriod.uuid);

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - CREATE
    static async storeNewPeriod(period) {
        const requiredFields = pick(period, ['uuid', 'title', 'order', 'period_order', 'weeks', 'type']);
        try {
            const { response } = await TermTemplatePeriod.api().post(
                `/api/term-templates/${period.term_schedule_template_id}/periods`,
                {
                    ...requiredFields,
                }
            );

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return true;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async addNewPeriod(termTemplate, type) {
        const index = termTemplate.periods.length;
        const titleIndex = termTemplate.periods.filter(item => item.type === 'period').length + 1;

        const newPeriod = await new TermTemplatePeriod({
            title: type === 'period' ? `Term ${titleIndex}` : 'Term Break',
            order: index,
            period_order: type === 'period' ? titleIndex : null,
            type,
            term_schedule_template_id: termTemplate.id,
        }).$save();

        await this.storeNewPeriod(newPeriod);

        return newPeriod;
    }

    static async reorder(termId, updatedPeriods) {
        const endpoint = `/api/term-templates/${termId}/periods/reorder`;
        try {
            const { response } = await this.api().post(endpoint, {
                periods: updatedPeriods,
            });

            if (response.status >= 200 && response.status < 300) {
                await this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    validateWeeksLimits(startWeek, expectedInstructionWeeks) {
        if (startWeek > 1) {
            const sequence = new Array(expectedInstructionWeeks).fill().map((_, i) => i + 1);

            const startWeekIndex = sequence.indexOf(startWeek);

            // Returning the Course Length.
            return sequence.slice(startWeekIndex).length;
        }

        return 0;
    }

    get isBreak() {
        return this.type === 'break';
    }

    get isCourse() {
        return this.type === 'course';
    }

    get isPeriod() {
        return this.type === 'period';
    }

    get calendar_weeks() {
        if (this.type === 'break') {
            return this.weeks;
        }
        return this.courses.reduce((acc, course) => acc + course.duration, 0);
    }

    get some_courses() {
        return this.courses.filter(course => course.type !== 'break');
    }

    get some_breaks() {
        return this.courses.filter(course => course.type === 'break');
    }

    get formattedCourses() {
        return (
            this.some_courses?.map(course => ({
                ...course,
                duration_rate:
                    course.start_week === 1
                        ? course.duration + course.start_week - 1 + this.breaksDuration
                        : course.duration + course.start_week + this.breaksDuration,
            })) ?? []
        );
    }

    get maxDuration() {
        let maxWeek = 0;
        this.formattedCourses.forEach(course => {
            const splits = this.splitCourse(course);
            splits.forEach(split => {
                maxWeek = Math.max(maxWeek, split.start_week + split.duration - 1);
            });
        });
        return maxWeek;
    }

    splitCourse(course) {
        const splits = [];
        let currentStart = course.start_week;
        let remainingDuration = course.duration;
        const breakPeriods = this.some_breaks.sort((a, b) => a.start_week - b.start_week);

        breakPeriods.forEach(breakPeriod => {
            if (breakPeriod.start_week >= currentStart && breakPeriod.start_week < currentStart + remainingDuration) {
                const firstPartDuration = breakPeriod.start_week - currentStart;
                if (firstPartDuration > 0) {
                    splits.push({
                        ...course,
                        start_week: currentStart,
                        duration: firstPartDuration,
                    });
                    remainingDuration -= firstPartDuration;
                }
                currentStart = breakPeriod.start_week + breakPeriod.duration;
            }
        });

        if (remainingDuration > 0) {
            splits.push({
                ...course,
                start_week: currentStart,
                duration: remainingDuration,
            });
        }

        return splits;
    }

    splitCourseCopy(course) {
        const splits = [];
        let currentStart = course.start_week;
        let remainingDuration = course.duration;
        const breakPeriods = this.breaks.sort((a, b) => a.start_week - b.start_week);

        breakPeriods.forEach(breakPeriod => {
            if (breakPeriod.start_week >= currentStart && breakPeriod.start_week < currentStart + remainingDuration) {
                const firstPartDuration = breakPeriod.start_week - currentStart;
                if (firstPartDuration > 0) {
                    splits.push({
                        ...course,
                        start_week: currentStart,
                        duration: firstPartDuration,
                    });
                    remainingDuration -= firstPartDuration;
                }
                currentStart = breakPeriod.start_week + breakPeriod.duration;
            }
        });

        if (remainingDuration > 0) {
            splits.push({
                ...course,
                start_week: currentStart,
                duration: remainingDuration,
            });
        }

        return splits;
    }

    get calculated_calendar_weeks() {
        return this.type === 'break' ? this.weeks : this.maxDuration;
    }

    get breaks() {
        return this.courses.filter(course => course.type === 'break');
    }

    get actual_instruction_weeks() {
        if (this.type === 'break') {
            return 0;
        }

        const breakWeeks = this.breaks.reduce((weeks, course) => weeks + course.duration, 0);

        return this.someValue - breakWeeks;
    }

    get has_overtime() {
        if (this.type === 'break') {
            return false;
        }
        return this.actual_instruction_weeks > this.weeks;
    }

    get someValue() {
        let maxWeek = 0;
        this.formattedCourses.forEach(course => {
            const splits = this.splitCourseCopy(course);
            splits.forEach(split => {
                maxWeek = Math.max(maxWeek, split.start_week + split.duration - 1);
            });
        });
        return maxWeek;
    }
}
